import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Search, ShieldAlert, Activity, Users, KeyRound, FileText, RefreshCw, CheckCircle2, ChevronLeft, ChevronRight, Eye, Mail, Folder, FolderOpen, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 50;

export const logClientEvent = (action, extra = {}) => {
  axios.post(`${API}/audit/client-event`, { action, ...extra }).catch(() => {});
};

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div className={`rounded-xl border p-4 bg-black/40 ${accent || 'border-border/50'}`} data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <Icon className="w-4 h-4 text-primary/70" />
    </div>
    <p className="text-3xl font-bold mt-2 brand-heading">{value ?? '—'}</p>
  </div>
);

const roleColor = (r) => ({
  admin: 'bg-primary/15 text-primary border-primary/40',
  physician: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  management: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
}[r] || 'bg-muted/40 text-muted-foreground border-border/40');

const actionTone = (row) => {
  if (row.flagged) return 'text-rose-300';
  if (!row.success) return 'text-amber-300';
  if (/Deleted|Failed|blocked/i.test(row.action)) return 'text-amber-300';
  if (/Checked out|payment/i.test(row.action)) return 'text-emerald-300';
  return 'text-foreground';
};

const FolderView = ({ onOpenDay }) => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/audit/folders`).then(r => setData(r.data)).catch(() => {}); }, []);
  const download = async (date) => {
    try {
      const r = await axios.get(`${API}/audit/export`, { params: { date }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `audit-${date}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };
  if (!data) return <p className="text-xs text-muted-foreground">Loading folders…</p>;
  return (
    <div className="space-y-2" data-testid="audit-folders">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Daily audit folders · {data.folders.length} days · {data.total.toLocaleString()} events total</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[340px] overflow-y-auto pr-1">
        {data.folders.map(f => (
          <div key={f.date} className={`rounded-lg border-2 p-3 flex items-start gap-3 ${f.flagged ? 'border-rose-500/50 bg-rose-950/20' : 'border-primary/40 bg-black/30'}`} data-testid={`audit-folder-${f.date}`}>
            <Folder className={`w-8 h-8 shrink-0 ${f.flagged ? 'text-rose-300' : 'text-primary'}`} />
            <div className="min-w-0 flex-1">
              <p className="font-extrabold text-sm tracking-wide">{format(parseISO(f.date), 'EEE, MMM d, yyyy')}</p>
              <p className="text-[11px] text-muted-foreground">{f.count} event{f.count === 1 ? '' : 's'} · {f.users} staff{f.flagged ? <span className="text-rose-300"> · {f.flagged} flagged</span> : ''}{f.failed ? <span className="text-amber-300"> · {f.failed} failed</span> : ''}</p>
              <p className="text-[10px] text-muted-foreground/70">Started {format(parseISO(f.first_at), 'h:mm a')} · last {format(parseISO(f.last_at), 'h:mm a')}</p>
              <div className="flex gap-1 mt-1.5">
                <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 border-primary/40" onClick={() => onOpenDay(f.date)} data-testid={`audit-folder-open-${f.date}`}><FolderOpen className="w-3 h-3 mr-1" />Open</Button>
                <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" onClick={() => download(f.date)} data-testid={`audit-folder-download-${f.date}`}><Download className="w-3 h-3 mr-1" />CSV</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AuditLog = () => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [userId, setUserId] = useState('all');
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expanded, setExpanded] = useState(null);

  const [digestInfo, setDigestInfo] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  const sendDigest = async () => {
    setSendingDigest(true);
    try { const r = await axios.post(`${API}/audit/digest/send`); toast.success(`Digest emailed to ${r.data.sent_to}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to send digest'); }
    finally { setSendingDigest(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE, limit: PAGE };
      if (q.trim()) params.q = q.trim();
      if (userId !== 'all') params.user_id = userId;
      if (onlyFlagged) params.flagged = true;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [logs, sum, dig] = await Promise.all([
        axios.get(`${API}/audit/logs`, { params }),
        axios.get(`${API}/audit/summary`),
        axios.get(`${API}/audit/digest/preview`),
      ]);
      setRows(logs.data.items); setTotal(logs.data.total); setSummary(sum.data); setDigestInfo(dig.data);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  }, [page, q, userId, onlyFlagged, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [q, userId, onlyFlagged, dateFrom, dateTo]);

  const markReviewed = async (id) => {
    try { await axios.post(`${API}/audit/${id}/review`); toast.success('Marked as reviewed'); load(); }
    catch { toast.error('Failed to mark reviewed'); }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE));
  const users = summary?.per_user_7d || [];

  return (
    <div className="space-y-6" data-testid="audit-log">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold brand-heading tracking-wide">Access Audit Trail</h2>
          <p className="text-xs text-muted-foreground mt-1">Every login, chart open, edit, checkout and print — by who, when, and from where.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={sendDigest} disabled={sendingDigest} data-testid="audit-send-digest-btn" className="border-primary/40">
            <Mail className={`w-4 h-4 mr-1 ${sendingDigest ? 'animate-pulse' : ''}`} />Email digest now
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="audit-refresh-btn" className="border-primary/40"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        </div>
      </div>

      {digestInfo && (
        <p className="text-[11px] text-muted-foreground -mt-3" data-testid="audit-digest-schedule">
          Weekly digest emails every <span className="text-primary">{digestInfo.schedule.day} 8:00 AM</span> ({digestInfo.schedule.timezone}) to <span className="text-primary">{digestInfo.schedule.recipient}</span>
          {digestInfo.last?.last_sent_at ? ` · last sent ${format(parseISO(digestInfo.last.last_sent_at), 'MMM d, h:mm a')}` : ' · not sent yet'}
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard icon={Folder} label="Total events" value={summary?.total_events?.toLocaleString()} accent="border-primary/60 bg-primary/5" testId="audit-stat-total" />
        <StatCard icon={Activity} label="Events · 24h" value={summary?.events_24h} testId="audit-stat-events" />
        <StatCard icon={ShieldAlert} label="Flagged · unreviewed" value={summary?.flagged_open} accent={summary?.flagged_open ? 'border-rose-500/60 bg-rose-950/30' : 'border-border/50'} testId="audit-stat-flagged" />
        <StatCard icon={KeyRound} label="Failed logins · 24h" value={summary?.failed_logins_24h} testId="audit-stat-failed" />
        <StatCard icon={Users} label="Active users · 24h" value={summary?.active_users_24h} testId="audit-stat-users" />
        <StatCard icon={FileText} label="Chart opens · 7d" value={summary?.chart_opens_7d} testId="audit-stat-charts" />
      </div>

      <FolderView onOpenDay={(d) => { setDateFrom(d); setDateTo(d); }} />

      {users.length > 0 && (
        <Card className="bg-black/30 border-border/50">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Activity by staff · last 7 days</p>
            <div className="flex flex-wrap gap-2">
              {users.map(u => (
                <button key={u.user_id} onClick={() => setUserId(userId === u.user_id ? 'all' : u.user_id)}
                  className={`text-left rounded-lg border px-3 py-2 transition-colors ${userId === u.user_id ? 'border-primary bg-primary/10' : 'border-border/50 hover:border-primary/50'}`}
                  data-testid={`audit-user-chip-${u.user_id}`}>
                  <p className="text-sm font-medium">{u.name || u.email}</p>
                  <p className="text-[11px] text-muted-foreground">{u.events} events · {u.chart_opens} charts{u.flagged ? <span className="text-rose-300"> · {u.flagged} flagged</span> : ''}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search staff, patient, or action…" className="pl-9" data-testid="audit-search" />
        </div>
        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger className="w-[200px]" data-testid="audit-user-filter"><SelectValue placeholder="All staff" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All staff</SelectItem>
            {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[150px]" data-testid="audit-date-from" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[150px]" data-testid="audit-date-to" />
        <Button size="sm" variant={onlyFlagged ? 'default' : 'outline'} onClick={() => setOnlyFlagged(v => !v)} data-testid="audit-flagged-toggle"
          className={onlyFlagged ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10'}>
          <ShieldAlert className="w-4 h-4 mr-1" />Flagged only
        </Button>
      </div>

      <div className="rounded-xl border border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-black/40">
              <TableHead className="w-[170px]">When</TableHead>
              <TableHead>Staff</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Patient</TableHead>
              <TableHead className="w-[120px]">IP</TableHead>
              <TableHead className="w-[110px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">{loading ? 'Loading…' : 'No activity matches these filters.'}</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id} className={`${r.flagged && !r.reviewed ? 'bg-rose-950/30' : ''} cursor-pointer`} onClick={() => setExpanded(expanded === r.id ? null : r.id)} data-testid={`audit-row-${r.id}`}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{format(parseISO(r.created_at), 'MMM d, yyyy · h:mm:ss a')}</TableCell>
                <TableCell>
                  <p className="text-sm font-medium">{r.user_name || r.user_email || 'Unknown'}</p>
                  {r.user_role && <Badge variant="outline" className={`text-[9px] ${roleColor(r.user_role)}`}>{r.user_role}</Badge>}
                </TableCell>
                <TableCell>
                  <p className={`text-sm ${actionTone(r)}`}>{r.action}</p>
                  {r.flagged && <p className="text-[11px] text-rose-300 flex items-center gap-1 mt-0.5"><ShieldAlert className="w-3 h-3" />{r.flag_reason}</p>}
                  {!r.success && <p className="text-[11px] text-amber-300">Request failed (HTTP {r.status_code})</p>}
                  {expanded === r.id && (
                    <div className="mt-2 text-[11px] text-muted-foreground space-y-0.5" data-testid={`audit-row-details-${r.id}`}>
                      <p>{r.method} {r.path}</p>
                      {r.user_agent && <p className="truncate max-w-md">{r.user_agent}</p>}
                      {r.details && Object.keys(r.details).length > 0 && <p>{Object.entries(r.details).map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>}
                      {r.reviewed && <p className="text-emerald-300">Reviewed by {r.reviewed_by} · {r.reviewed_at ? format(parseISO(r.reviewed_at), 'MMM d, h:mm a') : ''}</p>}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm">{r.patient_name || (r.patient_id ? <span className="text-muted-foreground text-xs">{r.patient_id.slice(0, 8)}…</span> : <span className="text-muted-foreground/50">—</span>)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.ip || '—'}</TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  {r.flagged && !r.reviewed && (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" onClick={() => markReviewed(r.id)} data-testid={`audit-review-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" />Reviewed
                    </Button>
                  )}
                  {r.flagged && r.reviewed && <span className="text-[11px] text-emerald-300 flex items-center gap-1"><Eye className="w-3 h-3" />Reviewed</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span data-testid="audit-total">{total} event{total === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
          <span>Page {page + 1} of {pages}</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)} data-testid="audit-next-page"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
};
